import CheckIcon from "@redotech/redo-icons/check.svg";
import MinusIcon from "@redotech/redo-icons/minus.svg";
import classNames from "classnames";
import { memo, useEffect, useId, useRef } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { TextSizeValue } from "../../theme/typography";
import * as redoCheckboxCss from "./redo-checkbox.module.css";

export type CheckboxValue = boolean | "indeterminate";

export const redoCheckboxSize = ["xs", "sm", "md", "lg"] as const;
export type RedoCheckboxSize = (typeof redoCheckboxSize)[number];

export interface RedoCheckboxProps {
  "data-dd-action-name"?: string;
  dataTestId?: string;
  value: CheckboxValue;
  setValue(value: CheckboxValue): void;
  size?: RedoCheckboxSize;
  disabled?: boolean;
  label?: string;
  /** Rendered inline after the label, for badges and other trailing markers. */
  labelBadge?: React.ReactNode;
  description?: React.ReactNode;
  alignToLabel?: boolean;
  showLabelSpacer?: boolean;
  showDescriptionSpacer?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  labelStyle?: React.CSSProperties;
  ariaLabel?: string;
  uiSelector?: string;
  /** Pass `-1` when the checkbox is a visual-only control inside another widget. */
  tabIndex?: number;
}

export const RedoCheckbox = memo(function RedoCheckbox({
  dataTestId,
  value,
  setValue,
  disabled,
  label,
  labelBadge,
  description,
  size = "md",
  alignToLabel,
  showLabelSpacer,
  showDescriptionSpacer,
  onClick,
  labelStyle,
  ariaLabel,
  uiSelector,
  tabIndex,
  "data-dd-action-name": actionName,
}: RedoCheckboxProps) {
  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    event.stopPropagation();
    if (event.target.indeterminate) {
      setValue("indeterminate");
    } else if (event.target.checked) {
      setValue(true);
    } else {
      setValue(false);
    }
  }

  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = `redo-checkbox-${useId()}`;

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }
    inputRef.current.indeterminate = value === "indeterminate";
  }, [value, inputRef]);

  const checked = !!value;

  const Icon = value === "indeterminate" ? MinusIcon : CheckIcon;

  const labelColor = disabled || !checked ? "secondary" : "primary";
  const labelWeight = checked ? "medium" : "regular";
  const sizeClass = checkboxSizeToCssSize[size];

  return (
    <Flex
      align={alignToLabel ? "flex-start" : "center"}
      aria-label={ariaLabel}
      as="label"
      data-arbiter="RedoCheckbox"
      data-dd-action-name={actionName}
      data-ui={uiSelector}
      labelProps={{ htmlFor: inputId }}
    >
      <Flex
        align={alignToLabel ? "center" : "flex-start"}
        className={classNames(
          redoCheckboxCss.checkboxControl,
          alignToLabel && redoCheckboxCss.checkboxRow,
          alignToLabel && sizeClass,
        )}
      >
        <input
          aria-label={ariaLabel}
          checked={checked}
          className={redoCheckboxCss.input}
          data-dd-action-name={actionName}
          disabled={disabled}
          id={inputId}
          onChange={handleChange}
          onClick={onClick}
          ref={inputRef}
          tabIndex={tabIndex}
          type="checkbox"
        />
        <div
          className={classNames(
            redoCheckboxCss.box,
            sizeClass,
            disabled && redoCheckboxCss.disabled,
            checked && redoCheckboxCss.checked,
          )}
          data-testid={dataTestId}
        >
          <Icon
            className={classNames(
              redoCheckboxCss.icon,
              sizeClass,
              disabled && redoCheckboxCss.disabled,
              checked && redoCheckboxCss.checked,
            )}
          />
        </div>
      </Flex>
      {(label || description || labelBadge) && (
        <Flex dir="column" gap="none">
          {label ? (
            <Flex align="center" gap="xs">
              <Text
                className={redoCheckboxCss.text}
                fontSize={checkboxSizeToLabelSize[size]}
                fontWeight={labelWeight}
                style={labelStyle}
                textColor={labelColor}
              >
                {label}
              </Text>
              {labelBadge}
            </Flex>
          ) : (
            showLabelSpacer && (
              <span
                className={classNames(redoCheckboxCss.labelSpacer, sizeClass)}
              />
            )
          )}
          {description ? (
            typeof description === "string" ? (
              <Text
                className={redoCheckboxCss.text}
                fontSize={checkboxSizeToDescriptionSize[size]}
                textColor="secondary"
              >
                {description}
              </Text>
            ) : (
              description
            )
          ) : (
            showDescriptionSpacer && (
              <span className={redoCheckboxCss.descriptionSpacer} />
            )
          )}
        </Flex>
      )}
    </Flex>
  );
});

const checkboxSizeToCssSize: Record<RedoCheckboxSize, string> = {
  xs: redoCheckboxCss.small ?? "",
  sm: redoCheckboxCss.small ?? "",
  md: redoCheckboxCss.medium ?? "",
  lg: redoCheckboxCss.large ?? "",
};

const checkboxSizeToLabelSize: Record<RedoCheckboxSize, TextSizeValue> = {
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
};

const checkboxSizeToDescriptionSize: Record<RedoCheckboxSize, TextSizeValue> = {
  xs: "xs",
  sm: "sm",
  md: "sm",
  lg: "md",
};
